// src/components/CartIcon.jsx
import React, { useContext, useState, useEffect, useRef } from "react";
import { ShopContext } from "../context/shopContext";
import { useNavigate } from "react-router-dom";

function formatMoney(amount, currencyCode) {
  const n = Number(amount ?? 0);
  try {
    const isWhole = Math.abs(n - Math.round(n)) < 1e-6;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode || "GBP",
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: isWhole ? 0 : 2,
    }).format(n);
  } catch (e) {
    // fallback
    return `${currencyCode || "£"}${Math.floor(n)}`;
  }
}

// Force checkout to go to your Shopify shop
const SHOPIFY_CHECKOUT_DOMAIN = "b3deb6-2.myshopify.com";

function CartIcon() {
  const {
    cart,
    updateItemQuantity,
    removeItemFromCart,
    fetchProductByVariantId,
  } = useContext(ShopContext);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [cart]);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 1000);
  };

  const handleProductClick = async (variantId) => {
    const productId = await fetchProductByVariantId(variantId);
    if (productId) {
      const numericProductId = productId.split("/").pop();
      navigate(`/product/${numericProductId}`);
    } else {
      console.error("Unable to find product ID for variant:", variantId);
    }
  };

  // Total amount from cart cost (in cart's currency)
  const cartTotalAmount = cart?.cost?.totalAmount;
  const totalPrice = Number(cartTotalAmount?.amount ?? 0);

  // Currency: prefer cart total currency, then first line's currency, then GBP
  const firstLine = cart?.lines?.edges?.[0]?.node;
  const currencyFromCart = cart?.cost?.totalAmount?.currencyCode;
  const currencyFromLine =
    firstLine?.cost?.amountPerQuantity?.currencyCode ||
    firstLine?.merchandise?.price?.currencyCode;

  const currencyCode = currencyFromCart || currencyFromLine || "GBP";

  // Total count
  const itemCount = (cart?.lines?.edges || []).reduce(
    (sum, edge) => sum + (edge.node.quantity || 0),
    0
  );

  // Build a safe checkout URL on the Shopify domain
  let checkoutHref = "";
  if (cart?.checkoutUrl) {
    try {
      const url = new URL(cart.checkoutUrl);

      // ALWAYS force hostname to Shopify checkout domain
      url.hostname = SHOPIFY_CHECKOUT_DOMAIN;

      checkoutHref = url.toString();
    } catch (e) {
      console.error("Invalid checkout URL from Shopify:", cart.checkoutUrl, e);
      checkoutHref = cart.checkoutUrl; // fallback
    }
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Cart Icon */}
      <div className="flex items-center relative text-xs">
        <span className="italic uppercase">Bag</span>
        <span className="ml-1">({itemCount})</span>
      </div>

      {/* Cart Details Popup */}
      {isDropdownOpen && (
        <div
          className="absolute top-full right-0 mt-2 w-64 sm:w-80 bg-white shadow-lg border rounded-lg p-4 z-50"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {(cart?.lines?.edges || []).length > 0 ? (
            <div>
              <h4 className="text-lg mb-2 italic">Your Cart</h4>
              <ul className="space-y-2">
                {cart.lines.edges.map(({ node: item }) => {
                  if (!item || !item.merchandise) return null;

                  const productTitle =
                    item.merchandise?.product?.title || "Unknown Product";

                  const size =
                    item.merchandise?.selectedOptions?.find(
                      (opt) => opt.name === "Size"
                    )?.value || null;

                  const unitAmount = Number(
                    item?.cost?.amountPerQuantity?.amount ??
                      item?.merchandise?.price?.amount ??
                      0
                  );

                  const lineCurrency =
                    item?.cost?.amountPerQuantity?.currencyCode ||
                    item?.merchandise?.price?.currencyCode ||
                    currencyCode;

                  const quantity = item.quantity || 0;
                  const variantId = item.merchandise.id;

                  return (
                    <li
                      key={item.id}
                      className="flex justify-between items-center"
                    >
                      <div>
                        <button
                          onClick={() => handleProductClick(variantId)}
                          className="text-customNavy hover:underline text-left"
                        >
                          {productTitle}
                        </button>
                        <p className="text-sm text-gray-500">
                          {quantity} × {formatMoney(unitAmount, lineCurrency)}
                        </p>
                        {size && (
                          <p className="text-sm text-gray-500">Size: {size}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          className="px-2 py-1 text-sm bg-gray-200 rounded"
                          onClick={() =>
                            updateItemQuantity(item.id, item.quantity - 1)
                          }
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span>{quantity}</span>
                        <button
                          className="px-2 py-1 text-sm bg-gray-200 rounded"
                          onClick={() =>
                            updateItemQuantity(item.id, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                        <button
                          className="text-customNavy text-sm"
                          onClick={() => removeItemFromCart(item.id)}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-right mt-2">
                Total: {formatMoney(totalPrice, currencyCode)}
              </p>

              {/* ✅ Use rewritten checkout URL */}
              {checkoutHref && (
                <a
                  href={checkoutHref}
                  rel="noopener noreferrer"
                  className="block w-full mt-4 px-6 py-3 text-center bg-customNavy text-white font-bold rounded hover:bg-white hover:text-customNavy transition hover:border hover:border-customNavy"
                >
                  Checkout
                </a>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 italic">
              Your cart is empty.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default CartIcon;
